<script lang="ts">
  import HomePage from "./lib/pages/HomePage.svelte";
  import DontGetWreckedPage from "./lib/pages/workshops/DontGetWreckedPage.svelte";
  import ToolsPage from "./lib/pages/tools/ToolsPage.svelte";
  import SailfishPage from "./lib/pages/sailfish/SailfishPage.svelte";
  import DocsPage from "./lib/pages/DocsPage.svelte";
  import ServicesPage from "./lib/pages/services/ServicesPage.svelte";
  import LearnIndex from "./lib/pages/blog/BlogIndex.svelte";
  import LearnPost from "./lib/pages/blog/BlogPost.svelte";
  import type { RouteConfig } from "@mateothegreat/svelte5-router";

  // Heavy pages loaded on demand (contain sailfish-sdk / @solana/web3.js)
  // Must be async functions — router checks constructor.name === "AsyncFunction"
  const lazySolanaTools = async () => import("./lib/pages/tools/SolanaToolsPage.svelte");
  const lazySolanaHistoric = async () => import("./lib/pages/tools/SolanaHistoricPage.svelte");
  const lazySolanaGrads = async () => import("./lib/pages/tools/SolanaGraduationsPage.svelte");
  const lazyWrapSol = async () => import("./lib/pages/tools/WrapSolPage.svelte");
  const lazyPolymarket = async () => import("./lib/pages/sailfish/PolymarketDemoPage.svelte");
  import { route, Router } from "@mateothegreat/svelte5-router";
  import { GithubIcon, MessageCircleIcon, TwitterIcon, SendIcon } from "lucide-svelte";
  import { onMount } from "svelte";

  // Scroll to top on route change
  onMount(() => {
    let lastPath = location.pathname;
    const observer = new MutationObserver(() => {
      if (location.pathname !== lastPath) {
        lastPath = location.pathname;
        window.scrollTo(0, 0);
      }
    });
    observer.observe(document.getElementById('app')!, { childList: true, subtree: true });
    window.addEventListener('popstate', () => window.scrollTo(0, 0));
    return () => observer.disconnect();
  });

  let routes: RouteConfig[] = $state([
    {
      path: "/",
      component: HomePage,
    },
    {
      path: "services",
      component: ServicesPage,
    },
    {
      path: "products/sailfish",
      component: SailfishPage,
    },
    {
      path: "products/sailfish/docs",
      component: DocsPage,
    },
    {
      path: "tools",
      component: ToolsPage,
    },
    {
      path: "tools/solana",
      component: lazySolanaTools,
    },
    {
      path: "tools/solana/historic",
      component: lazySolanaHistoric,
    },
    {
      path: "tools/solana/graduations",
      component: lazySolanaGrads,
    },
    {
      path: "tools/solana/wrap-sol",
      component: lazyWrapSol,
    },
    {
      path: "tools/polymarket",
      component: lazyPolymarket,
    },
    {
      path: "products/sailfish/solana",
      component: lazySolanaTools,
    },
    {
      path: "products/sailfish/polymarket",
      component: lazyPolymarket,
    },
    {
      path: "workshops/dont-get-wrecked",
      component: DontGetWreckedPage,
    },
    {
      path: "learn",
      component: LearnIndex,
    },
    {
      path: "learn/raydium-trading-data-api-tutorial",
      component: LearnPost,
    },
    {
      path: "learn/pumpfun-trades-graduations-api-tutorial",
      component: LearnPost,
    },
    {
      path: "learn/meteora-dex-trading-data-api-tutorial",
      component: LearnPost,
    },
    {
      path: "learn/hyperliquid-perpetuals-orderbook-api-tutorial",
      component: LearnPost,
    },
    {
      path: "learn/polymarket-orderbook-api-tutorial",
      component: LearnPost,
    },
    {
      path: "learn/how-to-build-solana-trading-bot",
      component: LearnPost,
    },
    {
      path: "learn/what-is-wrapped-sol",
      component: LearnPost,
    },
  ]);
</script>

<main class="bg-black min-h-screen flex flex-col">
  <!-- Sticky Navigation -->
  <nav class="bg-black/80 backdrop-blur-md border-b border-gray-800 sticky top-0 z-50">
    <div class="max-w-7xl mx-auto px-4 flex items-center justify-between h-16">
      <!-- Logo -->
      <a use:route href="/" class="flex items-center gap-2 hover:opacity-80 transition-opacity">
        <img src="/0xfire.webp" alt="0xfire logo" class="h-8 w-auto" width="32" height="32" />
        <span class="text-xl font-black text-white tracking-tight"><span class="text-orange-500">0x</span>fire</span>
      </a>

      <!-- Nav Links -->
      <div class="hidden md:flex items-center gap-8">
        <a use:route href="/products/sailfish" class="text-sm text-gray-400 hover:text-white transition-colors uppercase tracking-wider font-mono">
          Sailfish
        </a>
        <a use:route href="/services" class="text-sm text-gray-400 hover:text-white transition-colors uppercase tracking-wider font-mono">
          Services
        </a>
        <a use:route href="/tools" class="text-sm text-gray-400 hover:text-white transition-colors uppercase tracking-wider font-mono">
          Tools
        </a>
        <a use:route href="/learn" class="text-sm text-gray-400 hover:text-white transition-colors uppercase tracking-wider font-mono">
          Learn
        </a>
        <a href="https://t.me/+VXGdd5c6HOkzZjVi" target="_blank" class="text-gray-500 hover:text-white transition-colors" aria-label="Telegram">
          <SendIcon class="size-4" />
        </a>
      </div>

      <!-- CTA -->
      <a
        href="https://calendly.com/0xconstantin/30min"
        target="_blank"
        class="btn btn-sm bg-orange-600 hover:bg-orange-500 text-white border-none rounded-sm uppercase tracking-widest font-bold transition-colors"
      >
        Book a Call
      </a>
    </div>

    <!-- Mobile Menu -->
    <div class="md:hidden border-t border-gray-800 px-4 py-2 flex flex-wrap items-center gap-3">
      <a use:route href="/products/sailfish" class="text-xs text-gray-400 hover:text-white uppercase tracking-wider font-mono">Sailfish</a>
      <a use:route href="/services" class="text-xs text-gray-400 hover:text-white uppercase tracking-wider font-mono">Services</a>
      <a use:route href="/tools" class="text-xs text-gray-400 hover:text-white uppercase tracking-wider font-mono">Tools</a>
      <a use:route href="/learn" class="text-xs text-gray-400 hover:text-white uppercase tracking-wider font-mono">Learn</a>
      <a href="https://t.me/+VXGdd5c6HOkzZjVi" target="_blank" class="text-gray-500 hover:text-white transition-colors" aria-label="Telegram">
        <SendIcon class="size-3.5" />
      </a>
    </div>
  </nav>

  <!-- Routed Content -->
  <Router {routes} />
</main>

<footer class="bg-gray-950 border-t border-gray-800 px-10 py-12">
  <div class="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
    <aside>
      <div class="flex items-center gap-2 mb-2">
        <img src="/0xfire.webp" alt="0xfire logo" class="h-8 w-auto" width="32" height="32" />
        <p class="text-2xl font-black text-white">
          <span class="text-orange-500">0x</span>fire
        </p>
      </div>
      <p class="text-gray-400 text-sm">
        We build trading bots for a living
      </p>
    </aside>
    <nav>
      <p class="text-orange-500 uppercase tracking-widest text-xs font-mono mb-4 font-bold">Resources</p>
      <div class="flex flex-col gap-2">
        <a use:route href="/products/sailfish" class="text-gray-400 hover:text-gray-200 text-sm transition-colors">Sailfish</a>
        <a use:route href="/services" class="text-gray-400 hover:text-gray-200 text-sm transition-colors">Services</a>
        <a use:route href="/tools" class="text-gray-400 hover:text-gray-200 text-sm transition-colors">Tools</a>
        <a use:route href="/products/sailfish/docs" class="text-gray-400 hover:text-gray-200 text-sm transition-colors">Docs</a>
      </div>
    </nav>
    <nav>
      <p class="text-orange-500 uppercase tracking-widest text-xs font-mono mb-4 font-bold">Partners</p>
      <div class="flex flex-col gap-2">
        <a href="https://www.solanavibestation.com/" target="_blank" class="text-gray-400 hover:text-gray-200 text-sm transition-colors">Solana Vibestation</a>
        <a href="https://tradecraft.finance/" target="_blank" class="text-gray-400 hover:text-gray-200 text-sm transition-colors">TradeCraft</a>
      </div>
    </nav>
    <nav>
      <p class="text-orange-500 uppercase tracking-widest text-xs font-mono mb-4 font-bold">Community</p>
      <div class="flex flex-col gap-2">
        <a href="https://t.me/+VXGdd5c6HOkzZjVi" target="_blank" class="text-gray-400 hover:text-gray-200 text-sm transition-colors flex items-center gap-2">
          <SendIcon class="size-4" /> Telegram
        </a>
        <a href="https://discord.gg/cbtejQCpSh" target="_blank" class="text-gray-400 hover:text-gray-200 text-sm transition-colors flex items-center gap-2">
          <MessageCircleIcon class="size-4" /> Discord
        </a>
        <a href="https://github.com/0xfire-org/repositories" target="_blank" class="text-gray-400 hover:text-gray-200 text-sm transition-colors flex items-center gap-2">
          <GithubIcon class="size-4" /> GitHub
        </a>
        <a href="https://x.com/0xconstantin" target="_blank" class="text-gray-400 hover:text-gray-200 text-sm transition-colors flex items-center gap-2">
          <TwitterIcon class="size-4" /> Twitter
        </a>
      </div>
    </nav>
  </div>
</footer>
